/* eslint-disable react-hooks/exhaustive-deps */
import { Box, ButtonProps, useMediaQuery, useTheme } from "@mui/material";
import { GridAddIcon, GridDeleteIcon } from "@mui/x-data-grid";
import { useQueryClient } from "@tanstack/react-query";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { ReactComponent as Export } from "../../assets/icons/export.svg";
import { COLORS } from "../../assets/theme";
import Actions from "../../components/common/Actions";
import CustomDateRangePicker from "../../components/common/CustomDateRangePicker";
import CustomDialog from "../../components/common/CustomDialog";
import CustomLabel from "../../components/common/CustomLabel";
import SearchForm from "../../components/common/SearchForm";
import CustomTable from "../../components/common/table/CustomTable";
import SearchHeaderContainer from "../../components/common/table/SearchHeaderContainer";
import ToastNotification from "../../components/ToastNotification";
import { fetchCountry, useCountriesQuery } from "../../services/CountryService";
import { fetchOperator } from "../../services/OperatorsService";
import {
  fetchProvider,
  useProviderOptionQuery,
} from "../../services/ProviderService";
import {
  fetchRoutingPlan,
  useRoutingPlansQuery,
} from "../../services/RoutingPlanService";
import {
  addTpsRoutingApi,
  deleteTpsRoutingApi,
  editTpsRoutingApi,
  exportTpsRoute,
  fetchTpsHistory,
  getTpsRoutingById,
  TpsRouteType,
  TpsRoutingCreateAllApi,
  useTpsRoutingQuery,
} from "../../services/TPSRoutingService";
import {
  DateRange,
  IField,
  Option,
  Params,
} from "../../types/table/interfaces.type";
import { loadOperatorsByCountry } from "../../utils/commonDataUtils";
import History from "../../components/History";
import RelatedInstructionsDialog from "../../components/RelatedInstructionsDialog";
import AutoComplete, {
  IAutoCompleteProps,
} from "../../components/common/AutoComplete";
import { hasJwtPermission } from "../../services/ServicesHelpers";

const TPSRoutingPage: React.FC = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [appliedFilters, setAppliedFilters] = useState<
    Params<TpsRouteType & DateRange>
  >({
    search: "",
  });

  const { data: routingPlansOptions = [] } = useRoutingPlansQuery();
  const { data: providersOptions = [] } = useProviderOptionQuery();
  const { data: countriesOptions = [] } = useCountriesQuery();
  const RoutingPlanDetailsOptions = routingPlansOptions;
  const [operatorsByCountry, setOperatorsByCountry] = useState<Option[]>([]);
  const [isOperatorDisabled, setIsOperatorDisabled] = useState(true);
  const [filters, setFilters] = useState<
    Params<TpsRouteType & { countryId: string }>
  >({});
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);

  const dialogRef = useRef<TpsRoutingDialogRef>(null);
  const [relatedRow, setRelatedRow] = useState<TpsRouteType | null>(null);

  const handleOpenDialog = (
    mode: "add" | "edit" | "delete" | "history",
    id?: string,
    isClone = false,
  ) => {
    dialogRef.current?.openDialog(mode, id, isClone);
  };

  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  useEffect(() => {
    setSelectedRowIds([]);
  }, [filters]);

  const onRowSelect = (row: TpsRouteType, checked: boolean) => {
    setSelectedRowIds((prevSelected) => {
      const newSelected = checked
        ? [...prevSelected, String(row.id)]
        : prevSelected.filter((id) => id !== String(row.id));
      return newSelected;
    });
  };

  const isRowSelected = (row: TpsRouteType) =>
    selectedRowIds.includes(String(row.id));

  const handleSearchChange = (search: string) => {
    setAppliedFilters((prev) => ({ ...prev, search }));
  };

  const handleSearchClick = () => {
    setAppliedFilters((prev) => ({
      ...prev,
      fromDate: fromDate,
      toDate: toDate,
      operatorId: filters.operatorId,
      routingPlanId: filters.routingPlanId,
    }));
    setSelectedRowIds([]);
  };

  const actions = Actions({
    handleOpenEditModal: {
      handler: async (id: string | number) => {
        handleOpenDialog("edit", String(id));
      },
      isDisabled: !hasJwtPermission("GET_TPSROUTE"),
    },
    onCloneAction: {
      handler: async (id: string | number) => {
        handleOpenDialog("edit", String(id), true);
      },
      isDisabled: !hasJwtPermission("POST_TPSROUTE"),
    },
    // onDelete: {
    //   handler: async (id: string | number) => {
    //     const stringId = String(id);
    //     console.log(`Delete action triggered for ID: ${stringId}`);
    //     handleOpenDialog("delete", stringId);
    //   },
    //   isDisabled: false,
    // },
    onViewHistory: {
      // isDisabled: true,
      handler: async (id: number | string) => {
        handleOpenDialog("history", String(id));
      },
      isDisabled: !hasJwtPermission("GET_ALL_TPSROUTES_HISTORY"),
    },
    onCompareRelated: {
      tooltip: "TPS Routings on the same route",
      handler: (row: TpsRouteType) => setRelatedRow(row),
      isDisabled: (row: TpsRouteType) =>
        !hasJwtPermission("GET_ALL_TPSROUTES") ||
        !row?.routingPlanId ||
        !row?.operatorId,
    },
  });

  const autoCompletes: IAutoCompleteProps[] = [
    {
      label: "Routing Plan",
      value: filters.routingPlanId || "",
      options: RoutingPlanDetailsOptions,
      clearable: true,
      placeholder: "Select Routing Plan",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedPlan = RoutingPlanDetailsOptions.find(
          (option) => option.value === value,
        );
        setFilters((prev) => ({
          ...prev,
          routingPlanId: String(selectedPlan?.value ?? ""),
        }));
      },
    },
    {
      label: "Country",
      value: String(filters.countryId ?? ""),
      options: countriesOptions,
      placeholder: "Select Country",

      clearable: true,
      onChange: async (value: string | number | (string | number)[]) => {
        const selectedCountry = countriesOptions.find(
          (option) => option.value === value,
        );

        setFilters((prev) => ({
          ...prev,
          countryId: String(value),
          operatorId: undefined,
        }));

        if (selectedCountry) {
          try {
            setIsOperatorDisabled(true);
            const operators = await loadOperatorsByCountry(
              Number(selectedCountry.value),
            );
            setOperatorsByCountry(operators);
          } catch (error: any) {
            ToastNotification.error(error.message);

            setOperatorsByCountry([]);
          } finally {
            setIsOperatorDisabled(false);
          }
        }
      },
    },
    {
      label: "Operator",
      value: filters.operatorId ?? "",
      options: operatorsByCountry,
      disabled: isOperatorDisabled,

      clearable: true,
      placeholder: "Select Operator",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedOperator = operatorsByCountry.find(
          (option) => option.value === value,
        );

        setFilters((prev) => ({
          ...prev,
          operatorId: Number(selectedOperator?.value),
        }));
      },
    },
  ];

  const buttons = [
    {
      label: "Search",
      variant: "contained" as ButtonProps["variant"],
      bgColor: COLORS.GOLD,
      onClick: handleSearchClick,
      permission: "GET_ALL_TPSROUTES",
    },
  ];

  const fields = useMemo<IField<TpsRouteType>[]>(
    () => [
      {
        key: "routingPlanId",
        header: "Routing Plan",
        transform: async (t) => {
          if (!t.routingPlanId) return "-";
          try {
            const rp = await queryClient.fetchQuery({
              queryKey: ["routingPlan", t.routingPlanId],
              queryFn: () => fetchRoutingPlan(t.routingPlanId),
            });
            return rp?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        key: "operatorId",
        header: "Country",
        transform: async (t) => {
          if (!t.operatorId) return "-";
          try {
            const op = await queryClient.fetchQuery({
              queryKey: ["operator", t.operatorId],
              queryFn: () => fetchOperator(t.operatorId),
            });
            if (!op?.countryId) return "-";
            const country = await queryClient.fetchQuery({
              queryKey: ["country", op.countryId],
              queryFn: () => fetchCountry(op.countryId),
            });
            return country?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        key: "operatorId",
        header: "Operator",
        transform: async (t) => {
          if (!t.operatorId) return "-";
          try {
            const op = await queryClient.fetchQuery({
              queryKey: ["operator", t.operatorId],
              queryFn: () => fetchOperator(t.operatorId),
            });
            return op?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      { key: "tpsAbove", header: "TPS Above" },
      {
        key: "providerId",
        header: "Provider",
        transform: async (t) => {
          if (!t.providerId) return "-";
          try {
            const p = await queryClient.fetchQuery({
              queryKey: ["provider", t.providerId],
              queryFn: () => fetchProvider(t.providerId),
            });
            const providerName = p?.name ?? "-";
            const percentage = t.percentage0 ? ` (${t.percentage0}%)` : "";
            return `${providerName}${percentage}`;
          } catch {
            return "-";
          }
        },
      },
      {
        key: "provider1Id",
        header: "Provider 1",
        transform: async (t) => {
          if (!t.provider1Id) return "-";
          try {
            const p = await queryClient.fetchQuery({
              queryKey: ["provider", t.provider1Id],
              queryFn: () => fetchProvider(t.provider1Id),
            });
            return p?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      { key: "percentage1", header: "Percentage1" },
      {
        key: "provider2Id",
        header: "Provider 2",
        transform: async (t) => {
          if (!t.provider2Id) return "-";
          try {
            const p = await queryClient.fetchQuery({
              queryKey: ["provider", t.provider2Id],
              queryFn: () => fetchProvider(t.provider2Id),
            });
            return p?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      { key: "percentage2", header: "Percentage2" },
      {
        key: "provider3Id",
        header: "Provider 3",
        transform: async (t) => {
          if (!t.provider3Id) return "-";
          try {
            const p = await queryClient.fetchQuery({
              queryKey: ["provider", t.provider3Id],
              queryFn: () => fetchProvider(t.provider3Id),
            });
            return p?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      { key: "percentage3", header: "Percentage3" },
      { key: "effectiveDate", header: "Created Date" },
      { key: "createdBy", header: "User" },
    ],
    [queryClient],
  );

  const providerNameField = useCallback(
    async (providerId?: string) => {
      if (!providerId) return "-";
      try {
        const p = await queryClient.fetchQuery({
          queryKey: ["provider", providerId],
          queryFn: () => fetchProvider(providerId),
        });
        return p?.name ?? "-";
      } catch {
        return "-";
      }
    },
    [queryClient],
  );

  // Routing plan / country / operator are identical for every row of this view,
  // so only the fields that can actually differ are listed.
  const relatedFields = useMemo<IField<TpsRouteType>[]>(
    () => [
      { key: "name", header: "Name" },
      { key: "tpsAbove", header: "TPS Above" },
      {
        key: "providerId",
        header: "Provider",
        transform: async (t) => providerNameField(t.providerId),
      },
      { key: "percentage0", header: "Percentage 0" },
      {
        key: "provider1Id",
        header: "Provider 1",
        transform: async (t) => providerNameField(t.provider1Id),
      },
      { key: "percentage1", header: "Percentage 1" },
      {
        key: "provider2Id",
        header: "Provider 2",
        transform: async (t) => providerNameField(t.provider2Id),
      },
      { key: "percentage2", header: "Percentage 2" },
      {
        key: "provider3Id",
        header: "Provider 3",
        transform: async (t) => providerNameField(t.provider3Id),
      },
      { key: "percentage3", header: "Percentage 3" },
      { key: "effectiveDate", header: "Created Date" },
      { key: "createdBy", header: "User" },
    ],
    [providerNameField],
  );

  return (
    <Box padding={isSmallScreen ? 2 : 3}>
      <CustomLabel
        text="TPS Routing"
        variant={isSmallScreen ? "h6" : "h5"}
        fontWeight={500}
        marginTop={1}
        marginBottom={1}
      />
      <Box sx={{ mb: 3 }}>
        <SearchForm
          dateComponent={
            <CustomDateRangePicker
              fromDate={fromDate}
              toDate={toDate}
              setFromDate={setFromDate}
              setToDate={setToDate}
            />
          }
          autoCompletes={autoCompletes}
          buttons={buttons}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <CustomLabel
          text="TPS Routing List"
          variant={isSmallScreen ? "h6" : "h5"}
          fontWeight={500}
          marginTop={1}
          marginBottom={1}
        />
        <SearchHeaderContainer
          search={appliedFilters.search ?? ""}
          setSearch={handleSearchChange}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <GridAddIcon />,
              onClick: () => handleOpenDialog("add"),
              textColor: COLORS.iconColor,
              permission: "POST_TPSROUTE",
            },
            {
              size: "small",
              bgColor: COLORS.GOLD,
              variant: "outlined" as ButtonProps["variant"],
              startIcon: <GridDeleteIcon />,
              onClick: () => handleOpenDialog("delete"),
              textColor: COLORS.iconColor,
              permission: "DELETE_TPSROUTE",
            },
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <Export />,
              onClick: async () => {
                try {
                  await exportTpsRoute(appliedFilters);
                  ToastNotification.success("Export completed successfully!");
                } catch (error: any) {
                  ToastNotification.error(
                    error.message || "Export failed. Please try again.",
                  );
                }
              },
              showExportProgress: true,
              textColor: COLORS.iconColor,
              label: "Export",
              permission: "GET_ALL_TPSROUTES_CSV",
            },
          ]}
        />

        <CustomTable<TpsRouteType>
          filters={appliedFilters}
          fields={fields}
          fetchFunc={useTpsRoutingQuery}
          actions={actions}
          checkboxSelect={true}
          onRowSelect={onRowSelect}
          isRowSelected={isRowSelected}
          sortableFields={[
            "name",
            "tpsAbove",
            "createdBy",
            "effectiveDate",
            "percentage2",
            "percentage3",
            "percentage0",
            "percentage1",
          ]}
        />
      </Box>

      <TpsRoutingDialog
        ref={dialogRef}
        onRefresh={() => {
          setAppliedFilters((prev) => ({ ...prev }));
          queryClient.invalidateQueries({ queryKey: ["tpsRouting"] });
        }}
        selectedRowIds={selectedRowIds}
        onClearSelection={() => setSelectedRowIds([])}
        countriesOptions={countriesOptions}
        loadOperatorsByCountry={loadOperatorsByCountry}
        RoutingPlanDetailsOptions={RoutingPlanDetailsOptions}
        providersOptions={providersOptions}
        appliedFilters={appliedFilters}
      />
      <RelatedInstructionsDialog<TpsRouteType>
        open={!!relatedRow}
        onClose={() => setRelatedRow(null)}
        row={relatedRow}
        title="TPS Routings on the Same Route"
        fields={relatedFields}
        fetchFunc={useTpsRoutingQuery}
      />
    </Box>
  );
};

interface DialogData extends Omit<TpsRouteType, "operatorId"> {
  operatorId?: number | number[];
  countryId: string;
}

export interface TpsRoutingDialogRef {
  openDialog: (
    mode: "add" | "edit" | "delete" | "history",
    id?: string,
    isClone?: boolean,
  ) => void;
}

interface TpsRoutingDialogProps {
  onRefresh: () => void;
  countriesOptions: Option[];
  RoutingPlanDetailsOptions: Option[];
  providersOptions: Option[];
  selectedRowIds: string[];
  appliedFilters: Params<TpsRouteType>;
  onClearSelection: () => void;
  loadOperatorsByCountry: (countryId: number) => Promise<Option[]>;
}

const TpsRoutingDialog = forwardRef<TpsRoutingDialogRef, TpsRoutingDialogProps>(
  (
    {
      onRefresh,
      countriesOptions,
      RoutingPlanDetailsOptions,
      providersOptions,
      appliedFilters,
      loadOperatorsByCountry,
      selectedRowIds,
      onClearSelection,
    },
    ref,
  ) => {
    const queryClient = useQueryClient();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<
      "add" | "edit" | "delete" | "history"
    >("add");
    const [isCloneMode, setIsCloneMode] = useState(false);
    const [selectedId, setSelectedId] = useState<string | number>();
    const [dialogData, setDialogData] = useState<Partial<DialogData>>({});
    const [operatorsByCountry, setOperatorsByCountry] = useState<Option[]>([]);
    const [isOperatorDisabled, setIsOperatorDisabled] = useState(true);
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);
    const [errors, setErrors] = useState<{ [key: string]: boolean }>({});

    const validateField = (field: string, value: string) => {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: !value,
      }));
    };

    useImperativeHandle(ref, () => ({
      openDialog: (mode, id, isClone = false) => {
        setDialogMode(isClone ? "add" : mode);
        setSelectedId(isClone ? undefined : id);
        setIsCloneMode(isClone);
        setDialogOpen(true);
        openDialog(mode, id, isClone);
      },
    }));

    useEffect(() => {
      const validateFields = () => {
        const requiredFields: (keyof typeof dialogData)[] = [
          "name",
          "routingPlanId",
          "countryId",
          "operatorId",
          "tpsAbove",
          "providerId",
        ];

        const isAnyFieldEmpty = requiredFields.some(
          (field) => !dialogData[field],
        );

        setIsSaveDisabled(isAnyFieldEmpty);
      };

      validateFields();
    }, [dialogData]);

    const resetDialogData = () => {
      setDialogData({});
      setDialogOpen(false);
      setOperatorsByCountry([]);
      setIsSaveDisabled(true);
      setIsOperatorDisabled(true);
      setIsCloneMode(false);
    };

    const handleCountryChange = async (
      value: string | number | (string | number)[],
    ) => {
      const selectedCountry = Array.isArray(value) ? value[0] : value;
      setDialogData((prev) => ({
        ...prev,
        countryId: String(selectedCountry),
        operatorId: undefined,
      }));

      if (selectedCountry) {
        try {
          const countryId = Number(selectedCountry);
          if (isNaN(countryId)) {
            throw new Error("Invalid country ID");
          }

          setIsOperatorDisabled(false);
          const operators = await loadOperatorsByCountry(countryId);
          setOperatorsByCountry(operators);
        } catch (error: any) {
          ToastNotification.error(
            "Error fetching operators for selected country.",
          );
          setOperatorsByCountry([]);
        }
      } else {
        setOperatorsByCountry([]);
        setIsOperatorDisabled(true);
      }
    };

    const openDialog = async (
      mode: "add" | "edit" | "delete" | "history",
      id?: string,
      isClone = false,
    ) => {
      setDialogMode(isClone ? "add" : mode);

      try {
        if (mode === "edit" || isClone) {
          if (!id) {
            ToastNotification.error("ID is missing for editing.");
            return;
          }

          const tpsData = await getTpsRoutingById(id);

          // A clone keeps every value of the source route, only the name has to
          // be re-entered because it must stay unique.
          let countryId: number | undefined;

          if (tpsData.operatorId) {
            const { countryId: selectedCountry } = await fetchOperator(
              tpsData.operatorId,
            );

            if (selectedCountry) {
              countryId = Number(selectedCountry);
              setIsOperatorDisabled(true);
              const operators = await loadOperatorsByCountry(countryId);
              setOperatorsByCountry(operators);
              setIsOperatorDisabled(false);
            }
          }

          const sourceOperatorId = Array.isArray(tpsData.operatorId)
            ? tpsData.operatorId[0]
            : tpsData.operatorId;

          setDialogData({
            ...tpsData,
            ...(isClone ? { id: undefined, name: "" } : {}),
            countryId: countryId ?? "",
            operatorId:
              isClone && sourceOperatorId != null
                ? [Number(sourceOperatorId)]
                : sourceOperatorId,
          });
        } else if (mode === "delete" && id) {
          setDialogData({ id });
        } else if (mode === "history") {
          if (!id) {
            ToastNotification.error("ID is missing for viewing history.");
            return;
          }
          setDialogData({ id });
        } else {
          setDialogData({
            name: "",
            effectiveDate: new Date(),
          });
        }

        setDialogOpen(true);
      } catch (error: any) {
        console.error(
          `Error fetching TPS routing data for ${mode} mode:`,
          error,
        );
        ToastNotification.error(
          "An error occurred while fetching TPSrouting data.",
        );
      }
    };

    const addTpsRouting = async (data: Partial<DialogData>) => {
      try {
        const { operatorId, ...rest } = data;

        const filteredData = Object.entries(rest).reduce(
          (acc, [key, value]) => {
            if (value !== undefined && value !== null && value !== "") {
              acc[key] = value;
            }
            return acc;
          },
          {} as Record<string, any>,
        );

        if (Array.isArray(operatorId) && operatorId.length >= 1) {
          const payloads: Partial<TpsRouteType>[] = operatorId.map(
            (id, index) => ({
              ...filteredData,
              operatorId: id,
              name: data.name ? `${data.name}_${index + 1}` : undefined,
              type: "TPS_ROUTE",
              isDeleted: false,
            }),
          );

          await TpsRoutingCreateAllApi(payloads);
        }
        setDialogOpen(false);
        setDialogData({});
        onRefresh();
        ToastNotification.success("Tps routing added successfully!");
      } catch (error: any) {
        ToastNotification.error(error.message);
      }
    };

    const editTpsRouting = async (data: Partial<DialogData>) => {
      try {
        const id = data.id as string;
        if (!id) {
          ToastNotification.warning("Tps routing ID is missing.");
          return;
        }
        const { operatorId, percentage0, ...rest } = data;

        // Filter out empty provider and percentage fields
        const filteredData = Object.entries(rest).reduce(
          (acc, [key, value]) => {
            // Only include fields with values
            if (value !== undefined && value !== null && value !== "") {
              acc[key] = value;
            }
            return acc;
          },
          {} as Record<string, any>,
        );

        const payload: Partial<TpsRouteType> = {
          ...filteredData,
          operatorId: Array.isArray(operatorId) ? operatorId[0] : operatorId,
          type: "TPS_ROUTE",
          isDeleted: false,
        };

        await editTpsRoutingApi(id, payload);

        onRefresh();
        setDialogOpen(false);
        ToastNotification.success("Tps routing updated successfully!");
      } catch (error: any) {
        ToastNotification.error(error.message);
      }
    };

    const deleteTpsRouting = async () => {
      try {
        await deleteTpsRoutingApi(selectedRowIds);
        ToastNotification.success("Tps Route deleted successfully!");
        onClearSelection();
        onRefresh();
        setDialogOpen(false);
      } catch {
        ToastNotification.error("Error deleting Tps Route.");
      }
    };

    const fieldHistory = useMemo<
      IField<TpsRouteType & { historyTimestamp: Date }>[]
    >(
      () => [
        {
          key: "name",
          header: "Tps Name",
        },
        {
          key: "routingPlanId",
          header: "Routing Plan",
          transform: async (t) => {
            if (!t.routingPlanId) return "-";
            try {
              const rp = await queryClient.fetchQuery({
                queryKey: ["routingPlan", t.routingPlanId],
                queryFn: () => fetchRoutingPlan(t.routingPlanId),
              });
              return rp?.name ?? "-";
            } catch {
              return "-";
            }
          },
        },
        {
          key: "operatorId",
          header: "Country",
          transform: async (t) => {
            if (!t.operatorId) return "-";
            try {
              const op = await queryClient.fetchQuery({
                queryKey: ["operator", t.operatorId],
                queryFn: () => fetchOperator(t.operatorId),
              });
              if (!op?.countryId) return "-";
              const country = await queryClient.fetchQuery({
                queryKey: ["country", op.countryId],
                queryFn: () => fetchCountry(op.countryId),
              });
              return country?.name ?? "-";
            } catch {
              return "-";
            }
          },
        },
        {
          key: "operatorId",
          header: "Operator",
          transform: async (t) => {
            if (!t.operatorId) return "-";
            try {
              const op = await queryClient.fetchQuery({
                queryKey: ["operator", t.operatorId],
                queryFn: () => fetchOperator(t.operatorId),
              });
              return op?.name ?? "-";
            } catch {
              return "-";
            }
          },
        },
        { key: "tpsAbove", header: "TPS Above" },
        {
          key: "providerId",
          header: "Provider",
          transform: async (t) => {
            if (!t.providerId) return "-";
            try {
              const p = await queryClient.fetchQuery({
                queryKey: ["provider", t.providerId],
                queryFn: () => fetchProvider(t.providerId),
              });
              const providerName = p?.name ?? "-";
              const percentage = t.percentage0 ? ` (${t.percentage0}%)` : "";
              return `${providerName}${percentage}`;
            } catch {
              return "-";
            }
          },
        },
        {
          key: "provider1Id",
          header: "Provider 1",
          transform: async (t) => {
            if (!t.provider1Id) return "-";
            try {
              const p = await queryClient.fetchQuery({
                queryKey: ["provider", t.provider1Id],
                queryFn: () => fetchProvider(t.provider1Id),
              });
              const providerName = p?.name ?? "-";
              const percentage = t.percentage1 ? ` (${t.percentage1}%)` : "";
              return `${providerName}${percentage}`;
            } catch {
              return "-";
            }
          },
        },
        {
          key: "provider2Id",
          header: "Provider 2",
          transform: async (t) => {
            if (!t.provider2Id) return "-";
            try {
              const p = await queryClient.fetchQuery({
                queryKey: ["provider", t.provider2Id],
                queryFn: () => fetchProvider(t.provider2Id),
              });
              const providerName = p?.name ?? "-";
              const percentage = t.percentage2 ? ` (${t.percentage2}%)` : "";
              return `${providerName}${percentage}`;
            } catch {
              return "-";
            }
          },
        },
        {
          key: "provider3Id",
          header: "Provider 3",
          transform: async (t) => {
            if (!t.provider3Id) return "-";
            try {
              const p = await queryClient.fetchQuery({
                queryKey: ["provider", t.provider3Id],
                queryFn: () => fetchProvider(t.provider3Id),
              });
              const providerName = p?.name ?? "-";
              const percentage = t.percentage3 ? ` (${t.percentage3}%)` : "";
              return `${providerName}${percentage}`;
            } catch {
              return "-";
            }
          },
        },
        { key: "effectiveDate", header: "Created Date" },
        { key: "createdBy", header: "User" },
        { key: "historyTimestamp", header: "Date Modified" },
        { key: "createdBy", header: "Created By" },
        { key: "updatedBy", header: "Updated By" },
      ],
      [queryClient],
    );

    return (
      <CustomDialog
        open={dialogOpen}
        onClose={resetDialogData}
        mode={dialogMode}
        title={
          dialogMode === "history"
            ? "History"
            : dialogMode === "delete"
              ? "Delete Tps Routing"
              : dialogMode === "edit"
                ? "Edit Tps Routing"
                : isCloneMode
                  ? "Clone Tps Routing"
                  : "Add Tps Routing"
        }
        fields={
          dialogMode === "history"
            ? [
                {
                  type: "custom",
                  component: (
                    <History
                      filters={appliedFilters}
                      fields={fieldHistory}
                      fetchFunc={async (params: any) => {
                        if (!selectedId) {
                          throw new Error("No item selected for history view");
                        }
                        const size = params?.size ?? 10;
                        const requestParams = Object.fromEntries(
                          Object.entries({
                            ...params,
                            size,
                            sort: "historyTimestamp,desc",
                          }).filter(([, value]) => {
                            if (value === null || value === undefined) {
                              return false;
                            }

                            if (typeof value === "string") {
                              return value.trim() !== "";
                            }

                            return true;
                          }),
                        );
                        const result = await fetchTpsHistory(
                          String(selectedId),
                          requestParams,
                        );
                        return {
                          ...result,
                          content: result.content.map((item) => ({
                            ...item,
                            historyTimestamp:
                              (item as any).historyTimestamp ?? new Date(),
                          })),
                        };
                      }}
                    />
                  ),
                },
              ]
            : dialogMode === "delete"
              ? [
                  {
                    type: "custom",
                    component: (
                      <Box>
                        <p>Are you sure you want to delete this route?</p>
                      </Box>
                    ),
                  },
                ]
              : [
                  {
                    type: "text",
                    label: "TPS Routing Name",
                    bgColor: COLORS.WhiteBackground,
                    highlightLabelStar: true,
                    value: dialogData.name ?? "",
                    validateOnChange: true,
                    onValidation: (isError) => {
                      setErrors((prevErrors) => ({
                        ...prevErrors,
                        name: isError,
                      }));
                    },
                    onChange: (value) => {
                      const trimmedValue = String(value).trimStart();
                      setDialogData((prev) => ({
                        ...prev,
                        name: trimmedValue,
                      }));
                    },
                    placeholder: "Session name",
                    error: !!errors.name,
                    helperText: errors.name ? "Name is required" : "",
                  },

                  {
                    type: "custom",
                    component: (
                      <AutoComplete
                        label="Routing Plan"
                        value={dialogData.routingPlanId ?? ""}
                        options={RoutingPlanDetailsOptions}
                        onChange={(
                          value: string | number | (string | number)[],
                        ) => {
                          setDialogData((prev) => ({
                            ...prev,
                            routingPlanId: String(value),
                          }));
                          validateField("routingPlan", String(value));
                        }}
                        clearable={true}
                        disablePortal={false}
                        error={!!errors.routingPlan}
                        placeholder="Select Routing Plan"
                        highlightLabelStar={true}
                        helperText={
                          errors.routingPlan ? "Routing Plan is required" : ""
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: COLORS.WhiteBackground,
                          },
                        }}
                      />
                    ),
                  },
                  {
                    type: "custom",
                    component: (
                      <AutoComplete
                        label="Country"
                        value={dialogData.countryId ?? ""}
                        options={countriesOptions}
                        onChange={handleCountryChange}
                        clearable={true}
                        disablePortal={false}
                        error={!!errors.country}
                        placeholder="Select Country"
                        highlightLabelStar={true}
                        helperText={errors.country ? "Country is required" : ""}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: COLORS.WhiteBackground,
                          },
                        }}
                      />
                    ),
                  },
                  {
                    type: "custom",
                    component: (
                      <AutoComplete
                        label="Operator"
                        multiple={dialogMode === "add"}
                        selectAll={dialogMode === "add"}
                        hideChips={dialogMode === "add"}
                        value={
                          dialogMode === "add"
                            ? Array.isArray(dialogData.operatorId)
                              ? dialogData.operatorId.map(String)
                              : []
                            : dialogData.operatorId !== undefined &&
                                dialogData.operatorId !== null
                              ? String(
                                  Array.isArray(dialogData.operatorId)
                                    ? dialogData.operatorId[0]
                                    : dialogData.operatorId,
                                )
                              : ""
                        }
                        options={operatorsByCountry}
                        onChange={(
                          value: string | number | (string | number)[],
                        ) => {
                          if (dialogMode === "add") {
                            const valueArray: number[] = Array.isArray(value)
                              ? value.map((v) => Number(v))
                              : value
                                ? [Number(value)]
                                : [];

                            setDialogData((prev) => ({
                              ...prev,
                              operatorId: valueArray,
                            }));
                          } else {
                            setDialogData((prev) => ({
                              ...prev,
                              operatorId:
                                value !== "" && value !== undefined
                                  ? Number(
                                      Array.isArray(value) ? value[0] : value,
                                    )
                                  : undefined,
                            }));
                          }
                        }}
                        clearable={true}
                        disablePortal={false}
                        error={!!errors.operatorId}
                        placeholder="Select Operator"
                        highlightLabelStar={true}
                        helperText={
                          errors.operatorId ? "Operator is required" : ""
                        }
                        disabled={isOperatorDisabled}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: COLORS.WhiteBackground,
                          },
                        }}
                      />
                    ),
                  },
                  {
                    type: "text",
                    label: "TpsAbove",
                    bgColor: COLORS.WhiteBackground,
                    highlightLabelStar: true,
                    value: dialogData.tpsAbove ?? "",
                    validateOnChange: true,
                    onValidation: (isError) => {
                      setErrors((prevErrors) => ({
                        ...prevErrors,
                        tpsAbove: isError,
                      }));
                    },
                    onChange: (value) => {
                      const numericValue =
                        value === "" ? undefined : Number(value);
                      setDialogData((prev) => ({
                        ...prev,
                        tpsAbove: numericValue,
                      }));
                    },
                    placeholder: "TPS Above",
                    error: !!errors.tpsAbove,
                    helperText: errors.tpsAbove ? "tps above is required" : "",
                  },
                  {
                    type: "custom",
                    component: (
                      <AutoComplete
                        label="Provider"
                        value={dialogData.providerId ?? ""}
                        options={providersOptions}
                        onChange={(
                          value: string | number | (string | number)[],
                        ) => {
                          setDialogData((prev) => ({
                            ...prev,
                            providerId: String(value),
                          }));
                          validateField("providerId", String(value));
                        }}
                        clearable={true}
                        disablePortal={false}
                        error={!!errors.providerId}
                        placeholder="Select Provider"
                        highlightLabelStar={true}
                        helperText={
                          errors.providerId ? "Provider is required" : ""
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: COLORS.WhiteBackground,
                          },
                        }}
                      />
                    ),
                  },
                  {
                    type: "combined",
                    label: "Blending",
                    showLabel: true,
                    fields: [
                      {
                        type: "custom",
                        component: (
                          <AutoComplete
                            value={dialogData.provider1Id ?? ""}
                            options={providersOptions}
                            onChange={(
                              value: string | number | (string | number)[],
                            ) => {
                              setDialogData((prev) => ({
                                ...prev,
                                provider1Id: Array.isArray(value)
                                  ? value.join(",")
                                  : String(value),
                              }));
                            }}
                            clearable={true}
                            disablePortal={false}
                            error={!!errors.provider1Id}
                            placeholder="Provider 1"
                            highlightLabelStar={true}
                            helperText={
                              errors.provider1Id ? "Provider is required" : ""
                            }
                            width="auto"
                            sx={{
                              flex: 1,
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: COLORS.WhiteBackground,
                              },
                            }}
                          />
                        ),
                      },
                      {
                        type: "text",
                        value: dialogData.percentage1 || "",
                        placeholder: "Percentage 1",
                        bgColor: COLORS.WhiteBackground,
                        onChange: (value: string | number) => {
                          const numericValue = Number(value);
                          const cappedValue =
                            numericValue > 100 ? 100 : numericValue;
                          setDialogData((prev) => ({
                            ...prev,
                            percentage1: cappedValue,
                          }));
                        },
                        error: !!errors.percentage1,
                        helperText: errors.percentage1
                          ? "Percentage 1 is required"
                          : "",
                      },
                    ],
                    layout: "row",
                    sx: { gap: 3 },
                    highlightLabelStar: true,
                  },
                  {
                    type: "combined",
                    fields: [
                      {
                        type: "custom",
                        component: (
                          <AutoComplete
                            value={dialogData.provider2Id ?? ""}
                            options={providersOptions}
                            onChange={(
                              value: string | number | (string | number)[],
                            ) => {
                              setDialogData((prev) => ({
                                ...prev,
                                provider2Id: Array.isArray(value)
                                  ? value.join(",")
                                  : String(value),
                              }));
                            }}
                            clearable={true}
                            disablePortal={false}
                            error={!!errors.provider2Id}
                            placeholder="Provider 2"
                            highlightLabelStar={true}
                            helperText={
                              errors.provider2Id ? "Provider is required" : ""
                            }
                            width="auto"
                            sx={{
                              flex: 1,
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: COLORS.WhiteBackground,
                              },
                            }}
                          />
                        ),
                      },
                      {
                        type: "text",
                        value: dialogData.percentage2 || "",
                        placeholder: "Percentage 2",
                        bgColor: COLORS.WhiteBackground,
                        onChange: (value: string | number) => {
                          const numericValue = Number(value);
                          const cappedValue =
                            numericValue > 100 ? 100 : numericValue;
                          setDialogData((prev) => ({
                            ...prev,
                            percentage2: cappedValue,
                          }));
                        },
                        error: !!errors.percentage2,
                        helperText: errors.percentage2
                          ? "Percentage 2 is required"
                          : "",
                      },
                    ],
                    layout: "row",
                    sx: { gap: 3 },
                    highlightLabelStar: true,
                  },
                  {
                    type: "combined",
                    fields: [
                      {
                        type: "custom",
                        component: (
                          <AutoComplete
                            value={dialogData.provider3Id ?? ""}
                            options={providersOptions}
                            onChange={(
                              value: string | number | (string | number)[],
                            ) => {
                              setDialogData((prev) => ({
                                ...prev,
                                provider3Id: Array.isArray(value)
                                  ? value.join(",")
                                  : String(value),
                              }));
                            }}
                            clearable={true}
                            disablePortal={false}
                            error={!!errors.provider3Id}
                            placeholder="Provider 3"
                            highlightLabelStar={true}
                            helperText={
                              errors.provider3Id ? "Provider is required" : ""
                            }
                            width="auto"
                            sx={{
                              flex: 1,
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: COLORS.WhiteBackground,
                              },
                            }}
                          />
                        ),
                      },
                      {
                        type: "text",
                        value: dialogData.percentage3 || "",
                        placeholder: "Percentage 3",
                        bgColor: COLORS.WhiteBackground,
                        onChange: (value: string | number) => {
                          const numericValue = Number(value);
                          const cappedValue =
                            numericValue > 100 ? 100 : numericValue;
                          setDialogData((prev) => ({
                            ...prev,
                            percentage3: cappedValue,
                          }));
                        },
                        error: !!errors.percentage3,
                        helperText: errors.percentage3
                          ? "Percentage 3 is required"
                          : "",
                      },
                    ],
                    layout: "row",
                    sx: { gap: 3 },
                    highlightLabelStar: true,
                  },
                ]
        }
        buttons={
          dialogMode === "history"
            ? []
            : [
                dialogMode !== "delete"
                  ? {
                      label: "Save",
                      variant: "contained",
                      bgColor: COLORS.GOLD,
                      borderRadius: "12px",
                      onClick: async () => {
                        if (dialogMode === "edit") {
                          await editTpsRouting(dialogData);
                        } else {
                          await addTpsRouting(dialogData);
                        }
                      },
                      disabled:
                        isSaveDisabled ||
                        (dialogMode === "edit"
                          ? !hasJwtPermission("PUT_TPSROUTE")
                          : !hasJwtPermission("POST_TPSROUTE")),
                    }
                  : {
                      label: "Delete",
                      variant: "contained",
                      bgColor: COLORS.GOLD,
                      textColor: COLORS.iconColor,
                      borderRadius: "12px",
                      onClick: deleteTpsRouting,
                      disabled: !hasJwtPermission("DELETE_TPSROUTE"),
                    },
              ]
        }
      />
    );
  },
);
export default TPSRoutingPage;
